/**
 * "Request access for a person" (5 Oct): the user access front door's wizard,
 * opened straight from the Access menu entry.
 *
 * "What do you need?" (the type and who it is for, one screen) → checklist →
 * forms → "Check and send". Anyone may use it — HR for a new hire, a manager
 * for a report, someone for themselves.
 *
 * Change: each ticked item's own Ivanti form, prefilled (the ordinary request
 * form in collect mode, so every required field still applies), all filed at
 * Send. New (product owner, 5 Oct): HR fills the New User Request only, and
 * for each other item says what is needed — a short note, and the roles if
 * they know them. Each item's owner (Backoffice, the manager, local IT)
 * completes its form from the case once the hire is in ITSM. Leave (6 Oct,
 * decision 10): the requester picks the leaver, the date and the hand-over,
 * fills the Deactivate User Request, and ticks the access to remove (ticked
 * from what ITSM shows they have); HR confirms unless HR asked, then each
 * team removes its part and local IT disables the account last.
 */

import { Check, Hourglass, Icon } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  cn,
  Field,
  Input,
  Radio,
  RadioGroup,
  Typography,
} from "@norican/react";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  FieldHighlightLegend,
  FormAnnouncer,
  HighlightedField,
  LoadingState,
  normaliseApiError,
  PageContainer,
  SectionCard,
  useDateFormatter,
  useFormAnnouncer,
  useFormCheckMessage,
  useSubmitLabel,
} from "@/components/common";
import {
  EmployeePicker,
  LookupSelect,
  ServiceRequestForm,
  useRequestOffering,
  useRoleLists,
  useTranslateTo,
} from "@/features/tickets";
import { useDebouncedValue, useDocumentTitle } from "@/hooks";
import { useLanguage, useT } from "@/i18n";
import {
  type AccessCaseSubmitResponse,
  type AccessCatalogItem,
  type AccessCatalogResponse,
  type AccessLeaver,
  type AccessNewHire,
  type AccessRoleList,
  type AccessWho,
  type ReferenceAccessResponse,
  useMe,
} from "@/lib/api";
import { fieldHighlightClass, useFieldHighlight } from "@/lib/field-highlight";
import {
  type FormCheckKey,
  isPastDate,
  shownCheck,
  stillNeededCount,
  todayIso,
} from "@/lib/form-checks";
import { logger } from "@/lib/logger";
import { revealFirstProblemSoon } from "@/lib/reveal-first-problem";

import {
  useAccessCatalog,
  useAccessDuplicate,
  useAccessPrefill,
  useReferenceAccess,
  useReferenceProfile,
  useResourceOwner,
  useSubmitAccessCase,
} from "./api";
import { useAccessHome } from "./components/access-home";
import { DuplicateNotice } from "./components/duplicate-notice";
import { HrPortalHint } from "./components/hr-portal-hint";
import { NewHireItemFields } from "./components/new-hire-item-fields";
import {
  ReferenceDiffView,
  ReferenceItemPanel,
  useEntryLabel,
} from "./components/reference-access";
import { RolePicker } from "./components/role-picker";
import {
  type AccessActionName,
  accessCasePath,
  type AccessTypeName,
  actionsChosen,
  askedHintKey,
  changeIntroKey,
  type CollectedForm,
  duplicateBlocks,
  duplicateFromParams,
  editPrefill,
  EMPTY_ROLES,
  formSequence,
  heldItems,
  helpdeskStartTypes,
  itemLabelKey,
  itemsForType,
  matchLocation,
  type NeedDetails,
  needDetails,
  needsFor,
  newHireMissing,
  newHireReady,
  onRecordItems,
  prefilledNoteKey,
  prefillItems,
  reviewLines,
  type RoleSelection,
  rolesForPrefill,
  rolesForSubmission,
  roleSummary,
  startType,
  stepOwnerKind,
  toSubmission,
  whoForType,
  type WhoKind,
  WIZARD_STEPS,
  wizardPrefillFromParams,
  type WizardStep,
  wizardStepNumber,
} from "./lib/access";
import {
  applyReference,
  EMPTY_CHOICE,
  entriesOf,
  hasDiff,
  type ReferenceChoice,
  type ReferenceDiff,
  referenceDiff,
  resourceText,
  systemsForItem,
} from "./lib/reference";

interface PersonChoice {
  id: string;
  name: string;
}

interface HireState {
  name: string;
  startDate: string;
  location: string;
  jobTitle: string;
  reportingTo: PersonChoice | null;
  referenceUser: PersonChoice | null;
  /** "No one similar – Backoffice will choose" (19 Oct, finding 6). */
  noReference: boolean;
  requestedFor: PersonChoice | null;
}

/** Leave (6 Oct): the leaving date and who takes over what. */
interface LeaverState {
  leavingDate: string;
  mailbox: PersonChoice | null;
  onedrive: PersonChoice | null;
  newManager: PersonChoice | null;
}

const EMPTY_LEAVER: LeaverState = {
  leavingDate: "",
  mailbox: null,
  onedrive: null,
  newManager: null,
};

const EMPTY_HIRE: HireState = {
  name: "",
  startDate: "",
  location: "",
  jobTitle: "",
  reportingTo: null,
  referenceUser: null,
  noReference: false,
  requestedFor: null,
};

function toWho(
  type: AccessTypeName,
  kind: WhoKind,
  hire: HireState,
  person: PersonChoice | null,
  leaver: LeaverState,
) {
  if (kind === "new_hire") {
    const newHire: AccessNewHire = {
      name: hire.name.trim(),
      start_date: hire.startDate === "" ? null : hire.startDate,
      primary_location: hire.location === "" ? null : hire.location,
      job_title: hire.jobTitle.trim() === "" ? null : hire.jobTitle.trim(),
      reporting_to: hire.reportingTo?.id ?? null,
      reference_user: hire.referenceUser?.id ?? null,
      requested_for: hire.requestedFor?.id ?? null,
    };
    return { type, new_hire: newHire, person_id: null } satisfies AccessWho;
  }
  if (type === "leave") {
    const details: AccessLeaver = {
      leaving_date: leaver.leavingDate === "" ? null : leaver.leavingDate,
      mailbox_delegate: leaver.mailbox?.id ?? null,
      onedrive_delegate: leaver.onedrive?.id ?? null,
      new_manager: leaver.newManager?.id ?? null,
    };
    return {
      type,
      new_hire: null,
      person_id: person?.id ?? null,
      leaver: details,
    } satisfies AccessWho;
  }
  return { type, new_hire: null, person_id: person?.id ?? null } satisfies AccessWho;
}

/** The answers the wizard keeps per checklist item. */
interface ItemAnswers {
  ticked: string[];
  actions: Record<string, AccessActionName | undefined>;
  notes: Record<string, string | undefined>;
  roles: Record<string, RoleSelection | undefined>;
  /** A new hire's software or folder (14 Oct): its form's other answers. */
  details: Record<string, NeedDetails | undefined>;
  /** 21 Oct: what was done with the reference user's entries, per item. */
  reference: Record<string, ReferenceChoice | undefined>;
  /** 21 Oct, Change: the colleague whose access is copied (optional). */
  referenceFrom: PersonChoice | null;
}

const EMPTY_ANSWERS: ItemAnswers = {
  ticked: [],
  actions: {},
  notes: {},
  roles: {},
  details: {},
  reference: {},
  referenceFrom: null,
};

/**
 * The reference user's access, for the checklist and the review (21 Oct):
 * who, what the server read, every system's role list, and the difference
 * per item with what was kept, removed and added.
 */
interface ReferenceContext {
  person: PersonChoice;
  data: ReferenceAccessResponse | undefined;
  isPending: boolean;
  isError: boolean;
  lists: Map<string, AccessRoleList>;
  diffs: Record<string, ReferenceDiff>;
  /** A Change: the person it is for (their entries say "has it already"). */
  compareName: string | null;
}

export function AccessRequestPage() {
  const { t } = useT("tickets");
  useDocumentTitle(t("access.wizard.title"));
  const { data: me } = useMe();
  const catalog = useAccessCatalog();

  // Opened from the chat's Access card or a suggestion chip (6 Oct): the
  // query carries what is already known. Read once, on open; the person can
  // change all of it here.
  const [searchParams] = useSearchParams();
  const [prefill] = useState(() => wizardPrefillFromParams(searchParams));
  const [step, setStep] = useState<WizardStep>("who");
  // No type from the chat (7 Oct, `type=ask`): nothing is picked, and the
  // first question decides. Everything past it reads `type`, which is New
  // until then; the person cannot get past the question without a pick.
  const [chosenType, setType] = useState<AccessTypeName | null>(
    prefill.type ?? (prefill.askType ? null : "new"),
  );
  // 28 Oct (Rusty): the HelpDesk starts only what the server says (Change by
  // default); New user and Leaver start in the HR Portal. A type it does not
  // start, from an old link or the chat, falls back to the one it does.
  const startTypes = helpdeskStartTypes(catalog.data?.helpdesk_starts);
  const type: AccessTypeName = startType(chosenType, startTypes);
  const [hire, setHire] = useState<HireState>(() => ({
    ...EMPTY_HIRE,
    name: prefill.type !== "change" && prefill.type !== "leave" ? (prefill.name ?? "") : "",
    // For Leave the date is the leaving date (below), not a start date.
    startDate: prefill.type === "leave" ? "" : (prefill.startDate ?? ""),
  }));
  const [person, setPerson] = useState<PersonChoice | null>(() =>
    prefill.type === "change" && prefill.personId && prefill.name
      ? { id: prefill.personId, name: prefill.name }
      : // Leave (6 Oct): the chat may know the leaver's id only; the name
        // comes with what ITSM shows they have, below.
        prefill.type === "leave" && prefill.personId
        ? { id: prefill.personId, name: prefill.name ?? "" }
        : null,
  );
  const [leaver, setLeaver] = useState<LeaverState>(() => ({
    ...EMPTY_LEAVER,
    leavingDate: prefill.type === "leave" ? (prefill.startDate ?? "") : "",
  }));
  const [allAnswers, setAnswers] = useState<ItemAnswers>(EMPTY_ANSWERS);
  // Leave: what ITSM shows the leaver has ticks the checklist, once per
  // leaver (the person can untick it), and names a leaver known by id only.
  const leaverId = type === "leave" ? (person?.id ?? null) : null;
  const leaverRecord = useReferenceProfile(leaverId);
  const [recordFor, setRecordFor] = useState<string | null>(null);
  const onRecord =
    leaverId !== null && leaverRecord.data !== undefined && catalog.data !== undefined
      ? onRecordItems(
          leaverRecord.data.access ?? [],
          (catalog.data.items ?? []).map((item) => item.key),
        )
      : [];
  if (
    leaverId !== null &&
    person !== null &&
    leaverRecord.data !== undefined &&
    recordFor !== leaverId &&
    catalog.data !== undefined
  ) {
    setRecordFor(leaverId);
    setAnswers((previous) => ({
      ...previous,
      ticked: [...previous.ticked, ...onRecord.filter((key) => !previous.ticked.includes(key))],
    }));
    if (person.name.trim() === "") {
      setPerson({ ...person, name: leaverRecord.data.person_name });
    }
  }
  // The site and the systems need the catalog: applied once, when it is here.
  const [prefillApplied, setPrefillApplied] = useState(false);
  if (!prefillApplied && catalog.data !== undefined) {
    setPrefillApplied(true);
    const keys = (catalog.data.items ?? []).map((item) => item.key);
    const ticked = prefillItems(prefill.items, keys);
    if (ticked.length > 0) {
      setAnswers({
        ...EMPTY_ANSWERS,
        ticked,
        actions:
          type === "change"
            ? Object.fromEntries(ticked.map((key) => [key, "add" as AccessActionName]))
            : {},
      });
    }
    const location = matchLocation(prefill.location, catalog.data.locations ?? []);
    if (location !== null) {
      setHire((previous) => ({ ...previous, location }));
    } else if (prefill.location && (catalog.data.locations ?? []).length === 0) {
      setHire((previous) => ({ ...previous, location: prefill.location ?? "" }));
    }
  }
  const [forms, setForms] = useState<Record<string, CollectedForm | undefined>>({});
  const [formIndex, setFormIndex] = useState(0);
  const [result, setResult] = useState<AccessCaseSubmitResponse | null>(null);
  // 7 Oct: IT or an admin said "a different person with the same name".
  const [override, setOverride] = useState(false);

  // 21 Oct: the reference user's access (New: the reference user), when the
  // switch is on. Nothing is read otherwise. 29 Oct (Rusty): no "Copy access
  // from a colleague" on a Change any more: it could not be done properly.
  const referenceOn = catalog.data?.reference_import === true && type === "new";
  const referencePerson = type === "new" ? hire.referenceUser : null;
  const referenceQuery = useReferenceAccess(referencePerson?.id ?? null, referenceOn);
  const extraLists = useRoleLists(referenceOn && referencePerson !== null);
  const genericLabel = t("access.reference.generic");

  const home = useAccessHome();
  const breadcrumbs = (
    <AppBreadcrumbs
      items={[{ label: home.label, to: home.to }, { label: t("access.wizard.title") }]}
    />
  );
  // The way to the cases list, top right (product owner, 5 Oct): the menu
  // entry opens this wizard, so the list needs a clear way in from here.
  const tracker = (
    <Button variant="outline" tone="neutral" size="sm" render={<Link to={home.to} />}>
      {home.linkLabel}
    </Button>
  );

  if (me !== undefined && me.access_front_door !== true) {
    return (
      <PageContainer title={t("access.wizard.title")} breadcrumbs={breadcrumbs}>
        <Alert tone="neutral">{t("access.off")}</Alert>
      </PageContainer>
    );
  }
  if (catalog.isPending) {
    return (
      <PageContainer title={t("access.wizard.title")} breadcrumbs={breadcrumbs} actions={tracker}>
        <LoadingState />
      </PageContainer>
    );
  }
  if (catalog.isError) {
    return (
      <PageContainer title={t("access.wizard.title")} breadcrumbs={breadcrumbs} actions={tracker}>
        <ErrorState error={catalog.error} onRetry={() => void catalog.refetch()} />
      </PageContainer>
    );
  }

  const kind = whoForType(type);
  // New hire: every item since 13 Oct (software and folders are part of the
  // New User process again). Anything a type does not offer is dropped here
  // so it is neither shown, nor asked, nor sent.
  const offered = itemsForType(type, catalog.data.items ?? []);
  const offeredCatalog = { ...catalog.data, items: offered };
  const order = offered.map((item) => item.key);
  const answers: ItemAnswers = {
    ...allAnswers,
    ticked: allAnswers.ticked.filter((key) => order.includes(key)),
    // New asks roles of 365FO only (its areas); a pick made under Change
    // before the type changed is not sent. 21 Oct: and of any item whose
    // reference user's access was copied in (its picks are the copy).
    roles:
      type === "new"
        ? Object.fromEntries(
            Object.entries(allAnswers.roles).filter(
              ([key]) => key === "365fo" || allAnswers.reference[key]?.applied === true,
            ),
          )
        : allAnswers.roles,
  };
  const referenceLists = new Map(
    [...(catalog.data.role_lists ?? []), ...(extraLists.data?.role_lists ?? [])].map((list) => [
      list.key,
      list,
    ]),
  );
  const referenceData = referenceQuery.data?.enabled === true ? referenceQuery.data : undefined;
  const referenceDiffs: Record<string, ReferenceDiff> = {};
  for (const key of order) {
    const choice = answers.reference[key];
    if (choice?.applied === true && referenceData !== undefined) {
      const systems = systemsForItem(referenceData.systems ?? [], key);
      referenceDiffs[key] = referenceDiff(
        systems,
        answers.roles[key] ?? EMPTY_ROLES,
        choice,
        referenceLists.get(systems[0]?.role_list ?? ""),
        genericLabel,
      );
    }
  }
  const reference: ReferenceContext | null =
    referenceOn && referencePerson !== null
      ? {
          person: referencePerson,
          data: referenceData,
          isPending: referenceQuery.isPending,
          isError: referenceQuery.isError,
          lists: referenceLists,
          diffs: referenceDiffs,
          compareName: null,
        }
      : null;
  const sequence = formSequence(type, order, answers.ticked);
  const who = toWho(type, kind, hire, person, leaver);
  const personName =
    kind === "new_hire" ? hire.name.trim() : (person?.name ?? t("access.wizard.me"));

  const current = sequence[formIndex];

  const resetForms = () => {
    setForms({});
    setFormIndex(0);
  };

  return (
    <PageContainer
      title={t("access.wizard.title")}
      subtitle={t("access.wizard.subtitle")}
      breadcrumbs={breadcrumbs}
      actions={tracker}
    >
      <div className="flex flex-col gap-6">
        {result === null ? <WizardProgress step={step} /> : null}
        {result !== null ? (
          <SentSection result={result} personName={personName} type={type} />
        ) : step === "who" ? (
          <WhoStep
            type={startTypes.length === 1 ? type : chosenType}
            startTypes={startTypes}
            hire={hire}
            person={person}
            leaver={leaver}
            onLeaver={(next) => {
              setLeaver(next);
              resetForms();
            }}
            override={override}
            onOverride={setOverride}
            locations={catalog.data.locations ?? []}
            hrPortalUrl={catalog.data.hr_portal_url ?? null}
            onType={(next) => {
              // The chat's systems were ticked before the type was known:
              // a Change adds them unless the person says otherwise.
              if (chosenType === null && next === "change") {
                setAnswers((previous) => ({
                  ...previous,
                  actions: Object.fromEntries(
                    previous.ticked.map((key) => [
                      key,
                      previous.actions[key] ?? ("add" as AccessActionName),
                    ]),
                  ),
                }));
              }
              setType(next);
              setOverride(false);
              resetForms();
            }}
            onHire={(next) => {
              if (next.referenceUser?.id !== hire.referenceUser?.id) {
                // 21 Oct: what was copied came from the previous reference user.
                setAnswers((previous) => ({ ...previous, reference: {} }));
              }
              setHire(next);
              if (next.name !== hire.name) {
                setOverride(false);
              }
              resetForms();
            }}
            onPerson={(next) => {
              setPerson(next);
              setOverride(false);
              resetForms();
            }}
            onNext={() => setStep("items")}
          />
        ) : step === "items" ? (
          <ItemsStep
            catalog={offeredCatalog}
            type={type}
            answers={answers}
            personName={personName}
            onRecord={onRecord}
            reference={reference}
            onAnswers={(next, changedItem) => {
              setAnswers(next);
              if (changedItem === null) {
                resetForms();
              } else {
                setForms((previous) => ({ ...previous, [changedItem]: undefined }));
              }
            }}
            onBack={() => setStep("who")}
            onNext={() => {
              setFormIndex(0);
              setStep("forms");
            }}
          />
        ) : step === "forms" && current !== undefined ? (
          <FormsStep
            key={current}
            who={who}
            itemKey={current}
            position={formIndex + 1}
            total={sequence.length}
            action={answers.actions[current] ?? null}
            ticked={answers.ticked}
            roles={answers.roles[current]}
            previous={forms[current] ?? null}
            onDone={(form) => {
              setForms((previous) => ({ ...previous, [current]: form }));
              if (formIndex + 1 < sequence.length) {
                setFormIndex(formIndex + 1);
              } else {
                setStep("review");
              }
            }}
            onBack={() => {
              if (formIndex === 0) {
                setStep("items");
              } else {
                setFormIndex(formIndex - 1);
              }
            }}
          />
        ) : (
          <ReviewStep
            catalog={offeredCatalog}
            who={who}
            type={type}
            sequence={sequence}
            forms={forms}
            answers={answers}
            personName={personName}
            managerName={hire.reportingTo?.name ?? null}
            leavingDate={type === "leave" && leaver.leavingDate !== "" ? leaver.leavingDate : null}
            onEdit={(index) => {
              setFormIndex(index);
              setStep("forms");
            }}
            onItems={() => setStep("items")}
            onWho={() => setStep("who")}
            overrideDuplicate={override && type === "new"}
            reference={reference}
            onSent={setResult}
          />
        )}
      </div>
    </PageContainer>
  );
}

// ─── Progress (19 Oct, UI review finding 6) ────────────────────────────────

/**
 * The four steps as a bar: Type & person → Access → Forms → Check & send,
 * the current one marked `aria-current="step"`. Before it only the Forms
 * step said "1 of 3", so nobody knew how far they were.
 */
function WizardProgress({ step }: { step: WizardStep }) {
  const { t } = useT("tickets");
  const current = wizardStepNumber(step);
  return (
    <nav aria-label={t("access.wizard.progress.label")}>
      <ol className="m-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-4">
        {WIZARD_STEPS.map((name, index) => {
          const number = index + 1;
          const state = number < current ? "done" : number === current ? "current" : "next";
          return (
            <li
              key={name}
              aria-current={state === "current" ? "step" : undefined}
              className={cn(
                "flex min-w-0 items-center gap-2 border-t-4 pt-2 text-sm",
                state === "next" ? "border-border text-fg-muted" : "border-accent text-fg",
                state === "current" ? "font-semibold" : null,
              )}
            >
              <span aria-hidden="true">{number}.</span>
              <span className="min-w-0 break-words">
                {t(`access.wizard.progress.${name}`)}
                {state === "done" ? (
                  <span className="sr-only">{` (${t("access.wizard.progress.done")})`}</span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ─── Who ───────────────────────────────────────────────────────────────────

interface WhoStepProps {
  /** None yet (7 Oct): opened from the chat without a type; only the question shows. */
  type: AccessTypeName | null;
  hire: HireState;
  person: PersonChoice | null;
  leaver: LeaverState;
  onLeaver: (leaver: LeaverState) => void;
  /** IT or an admin ticked "a different person with the same name" (7 Oct). */
  override: boolean;
  onOverride: (override: boolean) => void;
  locations: string[];
  /**
   * 21 Oct (Rusty): HR_PORTAL_URL, the link of the one-line "HR: … the HR
   * Portal" pointer for a New user or a Leaver (the HR list gate is retired).
   */
  hrPortalUrl: string | null;
  /** 28 Oct: the types the HelpDesk starts; one only (Change): no type question. */
  startTypes: readonly AccessTypeName[];
  onType: (type: AccessTypeName) => void;
  onHire: (hire: HireState) => void;
  onPerson: (person: PersonChoice | null) => void;
  onNext: () => void;
}

/**
 * "What do you need?" — the first screen (product owner, 5 Oct): a friendly
 * question rather than a form. The answer is the type (a new hire is New,
 * someone who works here is Change; Leave is coming), then who it is for.
 */
function WhoStep({
  type,
  hire,
  person,
  leaver,
  onLeaver,
  override,
  onOverride,
  locations,
  hrPortalUrl,
  startTypes,
  onType,
  onHire,
  onPerson,
  onNext,
}: WhoStepProps) {
  const { t } = useT("tickets");
  // No type yet (7 Oct, opened from the chat): nothing to check until one is picked.
  const typed = type ?? "new";
  const kind = whoForType(typed);
  // One open case per person and type (7 Oct): asked once the name has
  // settled, or as soon as a person is picked; Send asks again.
  const settledName = useDebouncedValue(hire.name.trim(), 500);
  const duplicateQuery =
    kind === "new_hire"
      ? { type: typed, name: settledName }
      : person !== null
        ? { type: typed, person_id: person.id }
        : { type: typed };
  const duplicateCheck = useAccessDuplicate(
    duplicateQuery,
    type !== null &&
      (kind === "new_hire"
        ? newHireReady({ name: settledName })
        : type === "change" || person !== null),
  );
  // A match for a name or person no longer on screen is not shown.
  const checkedFor =
    kind === "new_hire" ? settledName === hire.name.trim() && newHireReady(hire) : true;
  const duplicate = checkedFor ? (duplicateCheck.data?.duplicate ?? null) : null;
  const canOverride = duplicateCheck.data?.can_override === true;
  const duplicateBlocked = duplicateBlocks(duplicate, override, canOverride, type);
  // Field-state tint (5 Oct): the new hire's name is the one required field.
  const nameState = useFieldHighlight(null, hire.name, true);
  // …and for Leave, the leaver (7 Oct).
  const leaverState = useFieldHighlight(null, person?.id ?? null, type === "leave");

  /* Idiot-proof forms (7 Oct): Next says what is left instead of greying out,
     and a press with something missing or wrong takes the person to it. */
  const containerRef = useRef<HTMLDivElement>(null);
  const [attempted, setAttempted] = useState(false);
  /** Fields the person has left: the date check shows from then on (8 Oct). */
  const [left, setLeft] = useState<Record<string, boolean>>({});
  const leave = (key: string) => () =>
    setLeft((previous) => (previous[key] === true ? previous : { ...previous, [key]: true }));
  /** Person pickers with a name typed but nobody picked from the list. */
  const [unpicked, setUnpicked] = useState<Record<string, boolean>>({});
  const { message: announcement, announce } = useFormAnnouncer();
  const submitLabel = useSubmitLabel();
  const checkMessage = useFormCheckMessage();
  const pending = (key: string) => (next: boolean) =>
    setUnpicked((previous) => (previous[key] === next ? previous : { ...previous, [key]: next }));
  const pickers =
    kind === "new_hire"
      ? ["access-manager", "access-reference", "access-for"]
      : type === "leave"
        ? ["access-leaver", "access-mailbox", "access-onedrive", "access-new-manager"]
        : ["access-person"];
  const problems: Record<string, FormCheckKey> = {};
  for (const key of pickers) {
    if (unpicked[key] === true) {
      problems[key] = "notPicked";
    }
  }
  if (kind === "new_hire") {
    if (hire.name.trim() !== "" && !newHireReady(hire)) {
      problems["access-name"] = "tooShort";
    }
    // A start date already passed is almost always a typo (7 Oct).
    if (isPastDate(hire.startDate, todayIso())) {
      problems["access-start"] = "pastDate";
    }
  }
  // 19 Oct (finding 6): a New user needs its start date, manager and
  // reference user (or "No one similar") as well as the name. A picker with a
  // name typed but nobody picked is counted once, as "not picked".
  const missing = kind === "new_hire" ? newHireMissing(hire) : [];
  const stillNeeded = stillNeededCount(
    kind === "new_hire"
      ? missing.map((key) => unpicked[key] !== true)
      : type === "leave"
        ? [person === null && unpicked["access-leaver"] !== true]
        : [],
  );
  const requiredMessage: Record<string, string> = {
    "access-name": t("access.wizard.who.requiredName"),
    "access-start": t("access.wizard.who.requiredStart"),
    "access-manager": t("access.wizard.who.requiredManager"),
    "access-reference": t("access.wizard.who.requiredReference"),
  };
  const problemFor = (key: string): string | undefined =>
    checkMessage(
      shownCheck(problems[key], { touched: left[key] === true, attempted }),
      key === "access-name" ? 2 : undefined,
    ) ??
    (attempted && (missing as string[]).includes(key) && unpicked[key] !== true
      ? requiredMessage[key]
      : undefined);
  const highlight = (key: string) =>
    (missing as string[]).includes(key) ? ("required" as const) : null;
  const handleNext = () => {
    const toFix = Object.keys(problems).length + (duplicateBlocked ? 1 : 0);
    if (stillNeeded + toFix === 0) {
      onNext();
      return;
    }
    setAttempted(true);
    revealFirstProblemSoon(() => containerRef.current);
    announce(stillNeeded, toFix);
  };
  const setLeave = (patch: Partial<LeaverState>) => onLeaver({ ...leaver, ...patch });
  const pickLeave =
    (field: "mailbox" | "onedrive" | "newManager") =>
    (entry: { id: string; display_name: string } | null) =>
      setLeave({ [field]: entry === null ? null : { id: entry.id, name: entry.display_name } });
  const set = (patch: Partial<HireState>) => onHire({ ...hire, ...patch });
  const pick =
    (field: "reportingTo" | "referenceUser" | "requestedFor") =>
    (entry: { id: string; display_name: string } | null) =>
      set({ [field]: entry === null ? null : { id: entry.id, name: entry.display_name } });

  return (
    // 28 Oct: one type only (Change): no type question, so the card asks "Who is it for?".
    <SectionCard
      title={startTypes.length > 1 ? t("access.wizard.who.title") : t("access.wizard.who.forWhom")}
    >
      <div ref={containerRef} className="flex flex-col gap-5">
        {startTypes.length > 1 ? (
          <>
            <RadioGroup
              value={type ?? null}
              onValueChange={(value) => onType(value as AccessTypeName)}
              className="flex flex-col gap-2"
              aria-label={t("access.wizard.who.title")}
            >
              {startTypes.map((kind) => (
                <Radio.Root
                  key={kind}
                  value={kind}
                  label={t(
                    kind === "new"
                      ? "access.wizard.who.typeNew"
                      : kind === "leave"
                        ? "access.wizard.who.typeLeave"
                        : "access.wizard.who.typeChange",
                  )}
                />
              ))}
            </RadioGroup>
            <Typography variant="body-sm" tone="muted">
              {t(`access.wizard.type.hint.${type ?? "choose"}`)}
            </Typography>
            <HrPortalHint type={type} url={hrPortalUrl} />
          </>
        ) : null}
        {/* 28 Oct (Rusty): New user and Leaver start in the HR Portal; one line. */}
        {startTypes.includes("new") && startTypes.includes("leave") ? null : (
          <HrPortalHint type="new" url={hrPortalUrl} lead />
        )}
        {type === null ? null : (
          <>
            {startTypes.length > 1 ? (
              <Typography as="h3" variant="heading-6">
                {t("access.wizard.who.forWhom")}
              </Typography>
            ) : null}

            {kind === "new_hire" ? (
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <FieldHighlightLegend />
                </div>
                <HighlightedField state={nameState}>
                  <Field
                    name="access-name"
                    label={t("access.wizard.who.name")}
                    required
                    error={problemFor("access-name")}
                  >
                    <Input
                      value={hire.name}
                      maxLength={200}
                      onChange={(event) => set({ name: event.target.value })}
                    />
                  </Field>
                </HighlightedField>
                <HighlightedField state={highlight("access-start")}>
                  <Field
                    name="access-start"
                    label={t("access.wizard.who.startDate")}
                    required
                    error={problemFor("access-start")}
                  >
                    <Input
                      type="date"
                      value={hire.startDate}
                      onChange={(event) => set({ startDate: event.target.value })}
                      onBlur={leave("access-start")}
                    />
                  </Field>
                </HighlightedField>
                <Field name="access-location" label={t("access.wizard.who.location")}>
                  {locations.length === 0 ? (
                    <Input
                      value={hire.location}
                      maxLength={200}
                      onChange={(event) => set({ location: event.target.value })}
                    />
                  ) : (
                    <LookupSelect
                      items={locations.map((location) => ({
                        id: location,
                        label: { text: location },
                      }))}
                      value={hire.location === "" ? null : hire.location}
                      onValueChange={(next) => set({ location: next ?? "" })}
                      placeholder={t("access.wizard.who.location")}
                      aria-label={t("access.wizard.who.location")}
                    />
                  )}
                </Field>
                <Field name="access-title" label={t("access.wizard.who.jobTitle")}>
                  <Input
                    value={hire.jobTitle}
                    maxLength={200}
                    onChange={(event) => set({ jobTitle: event.target.value })}
                  />
                </Field>
                <HighlightedField state={highlight("access-manager")}>
                  <Field
                    name="access-manager"
                    label={t("access.wizard.who.reportingTo")}
                    hint={t("access.wizard.who.reportingToHint")}
                    required
                    error={problemFor("access-manager")}
                  >
                    <EmployeePicker
                      onTextPendingChange={pending("access-manager")}
                      name="access-manager"
                      value={hire.reportingTo?.id ?? null}
                      defaultDisplayValue={hire.reportingTo?.name ?? null}
                      onChange={pick("reportingTo")}
                      aria-label={t("access.wizard.who.reportingTo")}
                    />
                  </Field>
                </HighlightedField>
                <HighlightedField state={highlight("access-reference")}>
                  <div className="flex flex-col gap-2">
                    <Field
                      name="access-reference"
                      label={t("access.wizard.who.referenceUser")}
                      hint={t("access.wizard.who.referenceHint")}
                      required={!hire.noReference}
                      error={problemFor("access-reference")}
                    >
                      <EmployeePicker
                        onTextPendingChange={pending("access-reference")}
                        name="access-reference"
                        value={hire.referenceUser?.id ?? null}
                        defaultDisplayValue={hire.referenceUser?.name ?? null}
                        onChange={(entry) =>
                          set({
                            referenceUser:
                              entry === null ? null : { id: entry.id, name: entry.display_name },
                            noReference: entry === null ? hire.noReference : false,
                          })
                        }
                        aria-label={t("access.wizard.who.referenceUser")}
                        disabled={hire.noReference}
                      />
                    </Field>
                    <Checkbox.Root
                      checked={hire.noReference}
                      onCheckedChange={(next) =>
                        set({
                          noReference: next === true,
                          referenceUser: next === true ? null : hire.referenceUser,
                        })
                      }
                      label={t("access.wizard.who.noReference")}
                    />
                  </div>
                </HighlightedField>
                <Field
                  name="access-for"
                  label={t("access.wizard.who.onBehalf")}
                  hint={t("access.wizard.who.onBehalfHint")}
                  error={problemFor("access-for")}
                >
                  <EmployeePicker
                    onTextPendingChange={pending("access-for")}
                    name="access-for"
                    value={hire.requestedFor?.id ?? null}
                    defaultDisplayValue={hire.requestedFor?.name ?? null}
                    onChange={pick("requestedFor")}
                    aria-label={t("access.wizard.who.onBehalf")}
                  />
                </Field>
              </div>
            ) : type === "leave" ? (
              <div className="grid gap-5 sm:grid-cols-2">
                <HighlightedField state={leaverState}>
                  <Field
                    name="access-leaver"
                    label={t("access.wizard.who.leaver")}
                    required
                    error={problemFor("access-leaver")}
                  >
                    <EmployeePicker
                      onTextPendingChange={pending("access-leaver")}
                      name="access-leaver"
                      value={person?.id ?? null}
                      defaultDisplayValue={person?.name || null}
                      onChange={(entry) =>
                        onPerson(entry === null ? null : { id: entry.id, name: entry.display_name })
                      }
                      aria-label={t("access.wizard.who.leaver")}
                    />
                  </Field>
                </HighlightedField>
                <Field name="access-leaving" label={t("access.wizard.who.leavingDate")}>
                  <Input
                    type="date"
                    value={leaver.leavingDate}
                    onChange={(event) => setLeave({ leavingDate: event.target.value })}
                  />
                </Field>
                <Field
                  name="access-mailbox"
                  label={t("access.wizard.who.mailbox")}
                  error={problemFor("access-mailbox")}
                >
                  <EmployeePicker
                    onTextPendingChange={pending("access-mailbox")}
                    name="access-mailbox"
                    value={leaver.mailbox?.id ?? null}
                    defaultDisplayValue={leaver.mailbox?.name ?? null}
                    onChange={pickLeave("mailbox")}
                    aria-label={t("access.wizard.who.mailbox")}
                  />
                </Field>
                <Field
                  name="access-onedrive"
                  label={t("access.wizard.who.onedrive")}
                  error={problemFor("access-onedrive")}
                >
                  <EmployeePicker
                    onTextPendingChange={pending("access-onedrive")}
                    name="access-onedrive"
                    value={leaver.onedrive?.id ?? null}
                    defaultDisplayValue={leaver.onedrive?.name ?? null}
                    onChange={pickLeave("onedrive")}
                    aria-label={t("access.wizard.who.onedrive")}
                  />
                </Field>
                <Field
                  name="access-new-manager"
                  label={t("access.wizard.who.newManager")}
                  error={problemFor("access-new-manager")}
                >
                  <EmployeePicker
                    onTextPendingChange={pending("access-new-manager")}
                    name="access-new-manager"
                    value={leaver.newManager?.id ?? null}
                    defaultDisplayValue={leaver.newManager?.name ?? null}
                    onChange={pickLeave("newManager")}
                    aria-label={t("access.wizard.who.newManager")}
                  />
                </Field>
              </div>
            ) : (
              <Field
                name="access-person"
                label={t("access.wizard.who.person")}
                hint={t("access.wizard.who.personHint")}
                error={problemFor("access-person")}
              >
                <EmployeePicker
                  onTextPendingChange={pending("access-person")}
                  name="access-person"
                  value={person?.id ?? null}
                  defaultDisplayValue={person?.name ?? null}
                  onChange={(entry) =>
                    onPerson(entry === null ? null : { id: entry.id, name: entry.display_name })
                  }
                  aria-label={t("access.wizard.who.person")}
                />
              </Field>
            )}

            {duplicate !== null ? (
              <DuplicateNotice
                found={duplicate}
                blocking={duplicateBlocked}
                canOverride={canOverride}
                override={override}
                onOverride={onOverride}
              />
            ) : null}

            <StepButtons
              onNext={handleNext}
              nextLabel={submitLabel(t("access.wizard.next"), stillNeeded)}
            />
          </>
        )}
        <FormAnnouncer message={announcement} />
      </div>
    </SectionCard>
  );
}

// ─── Checklist ─────────────────────────────────────────────────────────────

function ItemsStep({
  catalog,
  type,
  answers,
  personName,
  onRecord,
  reference,
  onAnswers,
  onBack,
  onNext,
}: {
  catalog: AccessCatalogResponse;
  type: AccessTypeName;
  answers: ItemAnswers;
  personName: string;
  /** Leave: the items ITSM shows the leaver has. */
  onRecord: string[];
  /** 21 Oct: the reference user's access, when the switch is on and someone is picked. */
  reference: ReferenceContext | null;
  /** `changedItem`: only that item's form is stale; null: every form is. */
  onAnswers: (answers: ItemAnswers, changedItem: string | null) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { t } = useT("tickets");
  const { ticked, actions } = answers;
  const nurMissing = type === "new" && catalog.new_user_offering_id == null;
  const leaveMissing = type === "leave" && catalog.leave_offering_id == null;
  // New and Leave may tick nothing (the NUR, or only disabling the account).
  // Change needs at least one item, and Add or Remove on each (7 Oct: Next
  // says how many are left instead of greying out).
  const noneTicked = type === "change" && ticked.length === 0;
  const actionMissing = (key: string) => type === "change" && actions[key] === undefined;
  const stillNeeded = stillNeededCount([noneTicked, ...ticked.map(actionMissing)]);
  const containerRef = useRef<HTMLDivElement>(null);
  const { message: announcement, announce } = useFormAnnouncer();
  const submitLabel = useSubmitLabel();
  const handleNext = () => {
    if (stillNeeded === 0 && actionsChosen(type, ticked, actions)) {
      onNext();
      return;
    }
    revealFirstProblemSoon(() => containerRef.current);
    announce(stillNeeded, 0);
  };
  const lists = new Map((catalog.role_lists ?? []).map((list) => [list.key, list]));
  const refSystems = (key: string) =>
    reference?.data ? systemsForItem(reference.data.systems ?? [], key) : [];
  /** 21 Oct: the reference user's entries for an item, copied in (and the item ticked). */
  const copyIn = (keys: readonly string[]) => {
    let next = answers;
    for (const key of keys) {
      const systems = refSystems(key);
      const applied = applyReference(
        systems,
        next.roles[key] ?? EMPTY_ROLES,
        next.reference[key] ?? EMPTY_CHOICE,
      );
      const resource = (next.notes[key] ?? "").trim();
      next = {
        ...next,
        ticked: next.ticked.includes(key) ? next.ticked : [...next.ticked, key],
        actions:
          type === "change" && next.actions[key] === undefined
            ? { ...next.actions, [key]: "add" }
            : next.actions,
        roles: { ...next.roles, [key]: applied.selection },
        reference: { ...next.reference, [key]: applied.choice },
        notes:
          type === "new" && key === "shared_folders" && resource === ""
            ? { ...next.notes, [key]: resourceText(systems, applied.choice) }
            : next.notes,
      };
    }
    onAnswers(next, keys.length === 1 ? (keys[0] ?? null) : null);
  };
  const copyable = (catalog.items ?? [])
    .map((item) => item.key)
    .filter(
      (key) => entriesOf(refSystems(key)).length > 0 && answers.reference[key]?.applied !== true,
    );
  return (
    <SectionCard title={t("access.wizard.items.title")}>
      <div ref={containerRef} className="flex flex-col gap-4">
        {reference !== null ? (
          <ReferenceIntro
            reference={reference}
            copyable={copyable.length > 0}
            onCopyAll={() => copyIn(copyable)}
          />
        ) : null}
        {type === "new" ? (
          <>
            <Typography as="p" variant="body-sm" className="flex items-center gap-2">
              <Icon icon={Check} size="sm" tone="success" />
              {t("access.wizard.items.newUserIncluded")}
            </Typography>
            <Typography as="p" variant="body-sm" tone="muted">
              {t("access.wizard.items.newIntro", { name: personName })}
            </Typography>
          </>
        ) : null}
        {type === "leave" ? (
          <Typography as="p" variant="body-sm" tone="muted">
            {t("access.wizard.items.leaveIntro", { name: personName })}
          </Typography>
        ) : null}
        {nurMissing ? (
          <Alert tone="warning">
            {t("access.wizard.items.notInCatalog", { offering: catalog.new_user_offering })}
          </Alert>
        ) : null}
        {leaveMissing ? (
          <Alert tone="warning">
            {t("access.wizard.items.notInCatalog", {
              offering: catalog.leave_offering ?? "Deactivate User Request",
            })}
          </Alert>
        ) : null}
        {/* Change with nothing ticked: the list itself is "still needed". */}
        <ul
          className={cn(
            "m-0 flex list-none flex-col gap-4 p-0",
            noneTicked ? fieldHighlightClass("required") : null,
          )}
          data-highlight={noneTicked ? "required" : "none"}
        >
          {(catalog.items ?? []).map((item) => {
            // New and Leave: the owner files it, from their own catalog, so it
            // is not the requester's catalog that decides (5 Oct).
            const available = type !== "change" || item.offering_id != null;
            const checked = ticked.includes(item.key);
            // Leave removes everything in a system: no roles to pick. New
            // (7 Oct, UAT walkthrough): only 365FO asks, and only its areas;
            // Bluestar, Power BI and CRM are just ticked, and their roles are
            // the step owner's at "Open and complete".
            const list =
              item.roles_catalog && type !== "leave" && (type !== "new" || item.key === "365fo")
                ? lists.get(item.roles_catalog)
                : undefined;
            // 21 Oct: once the reference user's entries are copied in, the
            // system's own list adds more (Bluestar, Power BI included).
            const pickList =
              list ??
              (type !== "leave" && answers.reference[item.key]?.applied === true
                ? reference?.lists.get(refSystems(item.key)[0]?.role_list ?? "")
                : undefined);
            const formName = available
              ? item.offering
              : t("access.wizard.items.notInCatalog", { offering: item.offering });
            const onRecordNote =
              type === "leave" && onRecord.includes(item.key)
                ? ` · ${t("access.wizard.items.onRecord")}`
                : "";
            return (
              <li key={item.key} className="flex flex-col gap-2">
                {/* One line (7 Oct): the tick box, the system, its form muted. */}
                <span className="flex min-w-0 flex-wrap items-center gap-x-2">
                  <Checkbox.Root
                    checked={checked}
                    disabled={!available}
                    onCheckedChange={(next) =>
                      onAnswers(
                        {
                          ...answers,
                          ticked: next
                            ? [...ticked, item.key]
                            : ticked.filter((key) => key !== item.key),
                        },
                        null,
                      )
                    }
                    label={t(itemLabelKey(item.key) as never)}
                  />
                  <Typography as="span" variant="caption" tone="muted" className="min-w-0">
                    <span aria-hidden="true">· </span>
                    {formName}
                    {onRecordNote}
                  </Typography>
                </span>
                {reference !== null &&
                entriesOf(refSystems(item.key)).length > 0 &&
                answers.reference[item.key]?.applied !== true ? (
                  <ReferencePreview
                    personName={reference.person.name}
                    systems={refSystems(item.key)}
                    itemLabel={t(itemLabelKey(item.key) as never)}
                    disabled={!available}
                    onCopy={() => copyIn([item.key])}
                  />
                ) : null}
                {checked &&
                (type !== "new" ||
                  list !== undefined ||
                  stepOwnerKind(item) === "it_setup" ||
                  answers.reference[item.key]?.applied === true) ? (
                  <div className="flex flex-col gap-3 ps-7">
                    {reference !== null && answers.reference[item.key]?.applied === true ? (
                      <ReferenceItemPanel
                        name={`access-reference-${item.key}`}
                        personName={reference.person.name}
                        systems={refSystems(item.key)}
                        selection={answers.roles[item.key] ?? EMPTY_ROLES}
                        choice={answers.reference[item.key] ?? EMPTY_CHOICE}
                        diff={reference.diffs[item.key] ?? { kept: [], removed: [], added: [] }}
                        compareName={reference.compareName}
                        pickerBelow={pickList !== undefined}
                        onChange={(next) => {
                          const systems = refSystems(item.key);
                          const before = resourceText(
                            systems,
                            answers.reference[item.key] ?? EMPTY_CHOICE,
                          );
                          const resource = (answers.notes[item.key] ?? "").trim();
                          onAnswers(
                            {
                              ...answers,
                              roles: { ...answers.roles, [item.key]: next.selection },
                              reference: { ...answers.reference, [item.key]: next.choice },
                              // A folder list the person has not typed over follows the ticks.
                              notes:
                                type === "new" &&
                                item.key === "shared_folders" &&
                                (resource === "" || resource === before)
                                  ? {
                                      ...answers.notes,
                                      [item.key]: resourceText(systems, next.choice),
                                    }
                                  : answers.notes,
                            },
                            item.key,
                          );
                        }}
                      />
                    ) : null}
                    {type === "change" ? (
                      <HighlightedField state={actionMissing(item.key) ? "required" : null}>
                        <RadioGroup
                          value={actions[item.key] ?? ""}
                          onValueChange={(value) =>
                            onAnswers(
                              {
                                ...answers,
                                actions: { ...actions, [item.key]: value as AccessActionName },
                              },
                              item.key,
                            )
                          }
                          className="flex flex-wrap gap-4"
                          aria-label={t("access.wizard.items.actionLabel")}
                        >
                          <Radio.Root value="add" label={t("access.action.add")} />
                          <Radio.Root value="remove" label={t("access.action.remove")} />
                        </RadioGroup>
                      </HighlightedField>
                    ) : null}
                    {pickList !== undefined ? (
                      <RolePicker
                        name={`access-roles-${item.key}`}
                        list={pickList}
                        optional={type === "new"}
                        areasOnly={type === "new" && list !== undefined}
                        value={answers.roles[item.key] ?? EMPTY_ROLES}
                        onChange={(next) =>
                          onAnswers(
                            { ...answers, roles: { ...answers.roles, [item.key]: next } },
                            item.key,
                          )
                        }
                      />
                    ) : null}
                    {type === "new" && (item.new_hire_fields ?? []).length > 0 ? (
                      <NewHireItemFields
                        item={item}
                        resource={answers.notes[item.key] ?? ""}
                        details={answers.details[item.key] ?? {}}
                        onResource={(value) =>
                          onAnswers(
                            { ...answers, notes: { ...answers.notes, [item.key]: value } },
                            item.key,
                          )
                        }
                        onDetail={(field, value) =>
                          onAnswers(
                            {
                              ...answers,
                              details: {
                                ...answers.details,
                                [item.key]: { ...answers.details[item.key], [field]: value },
                              },
                            },
                            item.key,
                          )
                        }
                      />
                    ) : type === "new" ? (
                      <Field
                        name={`access-note-${item.key}`}
                        label={t("access.wizard.items.noteLabel")}
                      >
                        <Input
                          value={answers.notes[item.key] ?? ""}
                          maxLength={500}
                          onChange={(event) =>
                            onAnswers(
                              {
                                ...answers,
                                notes: { ...answers.notes, [item.key]: event.target.value },
                              },
                              item.key,
                            )
                          }
                        />
                      </Field>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
        <StepButtons
          onBack={onBack}
          onNext={handleNext}
          nextLabel={submitLabel(t("access.wizard.next"), stillNeeded)}
          // A form missing from the catalog is not the person's to fix: the
          // alert above says so, and Next stays off.
          nextDisabled={nurMissing || leaveMissing}
        />
        <FormAnnouncer message={announcement} />
      </div>
    </SectionCard>
  );
}

// ─── The reference user's access (21 Oct) ─────────────────────────────────

/**
 * "What <reference user> has": what it is, where it comes from, and
 * "Copy everything into this request". Each item then offers its own copy.
 */
function ReferenceIntro({
  reference,
  copyable,
  onCopyAll,
}: {
  reference: ReferenceContext;
  copyable: boolean;
  onCopyAll: () => void;
}) {
  const { t } = useT("tickets");
  const name = reference.person.name;
  return (
    <section
      aria-label={t("access.reference.title", { name })}
      className="border-border-subtle flex flex-col gap-2 rounded-md border p-3"
    >
      <Typography as="h3" variant="heading-6">
        {t("access.reference.title", { name })}
      </Typography>
      {reference.isPending ? (
        <LoadingState inline label={t("access.reference.loading", { name })} />
      ) : reference.isError || reference.data === undefined ? (
        <Typography as="p" variant="body-sm" tone="muted">
          {t("access.reference.error", { name })}
        </Typography>
      ) : (
        <>
          <Typography as="p" variant="body-sm" tone="muted">
            {t("access.reference.intro", { name })}
          </Typography>
          {copyable ? (
            <Button size="sm" variant="outline" className="self-start" onClick={onCopyAll}>
              {t("access.reference.copyAll")}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}

/** Under an item: "<name> has: Finance, Sales (+2)" and Copy. */
function ReferencePreview({
  personName,
  systems,
  itemLabel,
  disabled,
  onCopy,
}: {
  personName: string;
  systems: readonly { items?: { id: string; label: string }[] }[];
  itemLabel: string;
  disabled: boolean;
  onCopy: () => void;
}) {
  const { t } = useT("tickets");
  const label = useEntryLabel();
  const entries = systems.flatMap((system) => system.items ?? []);
  const shown = entries.slice(0, 4).map(label);
  const more = entries.length - shown.length;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-2 ps-7">
      <Typography as="span" variant="caption" tone="muted" className="min-w-0 break-words">
        {t("access.reference.has", { name: personName, list: shown.join(", ") })}
        {more > 0 ? ` ${t("access.reference.more", { count: more })}` : ""}
      </Typography>
      <Button
        size="sm"
        variant="outline"
        tone="neutral"
        disabled={disabled}
        aria-label={t("access.reference.copyNamed", { item: itemLabel, name: personName })}
        onClick={onCopy}
      >
        {t("access.reference.copy")}
      </Button>
    </span>
  );
}

// ─── One item's form ───────────────────────────────────────────────────────

function FormsStep({
  who,
  itemKey,
  position,
  total,
  action,
  ticked,
  roles,
  previous,
  onDone,
  onBack,
}: {
  who: AccessWho;
  itemKey: string;
  position: number;
  total: number;
  action: AccessActionName | null;
  ticked: string[];
  roles: RoleSelection | undefined;
  previous: CollectedForm | null;
  onDone: (form: CollectedForm) => void;
  onBack: () => void;
}) {
  const { t } = useT("tickets");
  const prefill = useAccessPrefill({
    who,
    item: itemKey,
    action,
    ticked,
    ...rolesForPrefill(roles),
  });
  const heading = (
    <Typography as="h2" variant="heading-5">
      {/* No "1 of 1" (7 Oct): the counter only when there is more than one form. */}
      {total > 1 ? `${t("access.wizard.forms.position", { position, total })} · ` : ""}
      {t(itemLabelKey(itemKey) as never)}
    </Typography>
  );
  if (prefill.isPending) {
    return (
      <>
        {heading}
        <LoadingState label={t("access.wizard.forms.loading")} />
      </>
    );
  }
  if (prefill.isError) {
    return (
      <>
        {heading}
        <ErrorState
          error={prefill.error}
          actions={
            <Button variant="outline" tone="neutral" onClick={onBack}>
              {t("access.wizard.back")}
            </Button>
          }
        />
      </>
    );
  }
  const data = prefill.data;
  const askedHint = askedHintKey(itemKey, data.asked_parameter_ids ?? []);
  const start = {
    ...(previous === null
      ? { offeringId: data.offering_id, subject: data.subject, values: data.values ?? [] }
      : editPrefill(previous, data.values ?? [])),
    prefilledNote: t(prefilledNoteKey(data.case_type)),
  };
  return (
    <>
      {heading}
      <Alert tone="info">{t("access.wizard.forms.banner")}</Alert>
      {askedHint ? <Alert tone="info">{t(askedHint as never)}</Alert> : null}
      {data.approver_field_label ? (
        <Alert tone="info">
          {t("access.approvals.formApprover", { field: data.approver_field_label })}
        </Alert>
      ) : null}
      <ServiceRequestForm
        starterPrefill={start}
        collect={{
          submitLabel: t("access.wizard.forms.save"),
          cancelLabel: t("access.wizard.back"),
          onCollect: onDone,
          onCancel: onBack,
          hiddenParameterIds: data.held_parameter_ids ?? [],
          // 28 Oct (Rusty): 365FO's company names its approvers, so it is required.
          requiredParameterIds: data.required_parameter_ids ?? [],
        }}
      />
    </>
  );
}

// ─── Check and send ────────────────────────────────────────────────────────

/** One answered form at review: its name, its team, Add/Remove and the key answers. */
function ReviewFormItem({
  label,
  item,
  form,
  action,
  onEdit,
}: {
  label: string;
  item: AccessCatalogItem | undefined;
  form: CollectedForm | undefined;
  action: AccessActionName | undefined;
  onEdit: () => void;
}) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const translateTo = useTranslateTo();
  const offering = useRequestOffering(form?.offeringId ?? null, translateTo);
  const lines = form ? reviewLines(offering.data?.parameters ?? [], form.answers) : [];
  return (
    <li className="border-border-subtle flex flex-col gap-1 rounded-md border p-3">
      <span className="flex flex-wrap items-center gap-2">
        <Typography as="span" variant="body-sm" className="font-medium">
          {label}
        </Typography>
        {action ? (
          <Badge tone="neutral" variant="subtle" size="xs">
            {t(`access.action.${action}`)}
          </Badge>
        ) : null}
        <Button variant="ghost" size="sm" className="ms-auto" onClick={onEdit}>
          {t("access.wizard.review.edit")}
        </Button>
      </span>
      {/* 28 Oct (Rusty): "Goes to: team" is enough; no offering name. */}
      {item?.team ? (
        <Typography as="span" variant="caption" tone="muted">
          {t("access.wizard.review.team", { team: item.team })}
        </Typography>
      ) : null}
      {lines.length > 0 ? (
        <dl className="m-0 grid gap-x-3 gap-y-1 sm:grid-cols-[minmax(0,14rem)_1fr]">
          {lines.map((line) => (
            <div key={line.label} className="contents">
              <dt className="text-fg-muted text-xs">{line.label}</dt>
              <dd className="m-0 text-xs whitespace-pre-line">
                {line.date ? formatDate(new Date(`${line.value}T12:00:00`)) : line.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </li>
  );
}

function ReviewStep({
  catalog,
  who,
  type,
  sequence,
  forms,
  answers,
  personName,
  managerName,
  leavingDate,
  onEdit,
  onItems,
  onWho,
  overrideDuplicate,
  reference,
  onSent,
}: {
  catalog: AccessCatalogResponse;
  who: AccessWho;
  type: AccessTypeName;
  sequence: string[];
  forms: Record<string, CollectedForm | undefined>;
  answers: ItemAnswers;
  personName: string;
  managerName: string | null;
  /** Leave: the leaving date (ISO), if given. */
  leavingDate: string | null;
  onEdit: (index: number) => void;
  onItems: () => void;
  /** Back to "Who is it for?" (a duplicate found at Send, 7 Oct). */
  onWho: () => void;
  overrideDuplicate: boolean;
  /** 21 Oct: the reference user's access and what was kept, removed and added. */
  reference: ReferenceContext | null;
  onSent: (result: AccessCaseSubmitResponse) => void;
}) {
  const { t } = useT("tickets");
  const { t: tErrors } = useT("errors");
  const { language } = useLanguage();
  const { formatDate } = useDateFormatter();
  const send = useSubmitAccessCase();
  const order = (catalog.items ?? []).map((item) => item.key);
  const byKey = new Map((catalog.items ?? []).map((item) => [item.key, item]));
  const lists = new Map((catalog.role_lists ?? []).map((list) => [list.key, list]));
  const held = heldItems(type, order, answers.ticked);
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const [partial, setPartial] = useState<AccessCaseSubmitResponse | null>(null);

  const body = {
    who,
    items: sequence.flatMap((key) => {
      const form = forms[key];
      return form === undefined
        ? []
        : [
            toSubmission(
              key,
              form,
              answers.actions[key] ?? null,
              language,
              rolesForSubmission(answers.roles[key]),
            ),
          ];
    }),
    needs:
      type === "new"
        ? needsFor(
            order,
            answers.ticked,
            answers.notes,
            answers.roles,
            answers.details,
            Object.fromEntries(
              (catalog.items ?? []).map((item) => [item.key, item.new_hire_fields ?? []]),
            ),
          ).map((need) => {
            // 21 Oct: the readable record of what was kept, removed and added.
            const diff = reference?.diffs[need.item];
            return diff !== undefined && hasDiff(diff) ? { ...need, reference: diff } : need;
          })
        : type === "leave"
          ? needsFor(order, answers.ticked, {}, {})
          : [],
    override_duplicate: overrideDuplicate,
  };
  // Send's 409 (7 Oct): an open case for the person, said as on the first step.
  const sendError = send.isError ? normaliseApiError(send.error) : null;
  const duplicate =
    sendError?.code === "ACCESS_CASE_DUPLICATE" ? duplicateFromParams(sendError.params) : null;

  const ownerOf = (item: AccessCatalogItem | undefined) => {
    // Leave: every removal goes to the item's team.
    const kind = item && type !== "leave" ? stepOwnerKind(item) : "team";
    if (kind === "it_setup") {
      return t("access.wizard.review.ownerItSetup");
    }
    if (kind === "manager") {
      return managerName ?? t("access.wizard.review.ownerManager");
    }
    return item?.team ?? "—";
  };

  const handleSend = () => {
    const fingerprint = JSON.stringify(body);
    if (attempt.current === null || attempt.current.fingerprint !== fingerprint) {
      attempt.current = { fingerprint, key: crypto.randomUUID() };
    }
    const idempotencyKey = attempt.current.key;
    void send.mutateAsync({ body, idempotencyKey }).then(
      (result) => {
        logger.debug("[access] sent", result.case_number, result.failed?.length ?? 0);
        if ((result.failed ?? []).length === 0) {
          onSent(result);
        } else {
          setPartial(result);
        }
      },
      (error: unknown) => logger.debug("[access] send failed", error),
    );
  };
  return (
    <SectionCard title={t("access.wizard.review.title")}>
      <div className="flex flex-col gap-4">
        <Typography as="p" variant="body-sm">
          {t(
            `access.wizard.review.intro.${
              type === "change"
                ? changeIntroKey(answers.ticked, answers.actions, catalog.items ?? [])
                : type
            }`,
            { name: personName },
          )}
        </Typography>
        <Typography as="p" variant="body-sm" className="font-medium">
          {t("access.wizard.review.forWho", { name: personName })}
          {type === "new" && managerName ? ` · ${t("access.manager", { name: managerName })}` : ""}
          {type === "leave" && leavingDate
            ? ` · ${t("access.leavingDate", { date: formatDate(new Date(`${leavingDate}T12:00:00`)) })}`
            : ""}
        </Typography>
        {reference !== null && Object.values(reference.diffs).some((diff) => hasDiff(diff)) ? (
          <section
            aria-label={t("access.reference.reviewTitle", { name: reference.person.name })}
            className="border-border-subtle flex flex-col gap-2 rounded-md border p-3"
          >
            <Typography as="h3" variant="body-sm" className="font-medium">
              {t("access.reference.reviewTitle", { name: reference.person.name })}
            </Typography>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {order
                .filter((key) => answers.ticked.includes(key))
                .filter((key) =>
                  hasDiff(reference.diffs[key] ?? { kept: [], removed: [], added: [] }),
                )
                .map((key) => (
                  <li key={key} className="flex flex-col gap-1">
                    <Typography as="span" variant="caption" className="font-medium">
                      {t(itemLabelKey(key) as never)}
                    </Typography>
                    <ReferenceDiffView
                      diff={reference.diffs[key] ?? { kept: [], removed: [], added: [] }}
                    />
                  </li>
                ))}
            </ul>
          </section>
        ) : null}
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {sequence.map((key, index) => (
            <ReviewFormItem
              key={key}
              label={t(itemLabelKey(key) as never)}
              item={byKey.get(key)}
              form={forms[key]}
              action={answers.actions[key]}
              onEdit={() => onEdit(index)}
            />
          ))}
        </ul>
        {held.length > 0 ? (
          <>
            <Typography as="h3" variant="heading-6">
              {type === "leave"
                ? t("access.wizard.review.leaveTitle")
                : t("access.wizard.review.laterTitle", { name: personName })}
            </Typography>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {held.map((key) => {
                const item = byKey.get(key);
                // 21 Oct: Bluestar and Power BI picks copied from the reference user.
                const list = item?.roles_catalog
                  ? lists.get(item.roles_catalog)
                  : answers.reference[key]?.applied === true
                    ? reference?.lists.get(key)
                    : undefined;
                const roles = roleSummary(
                  list,
                  answers.roles[key],
                  t("access.wizard.roles.otherShort"),
                );
                const note = [
                  (answers.notes[key] ?? "").trim(),
                  ...needDetails(item?.new_hire_fields ?? [], answers.details[key]).map(
                    (detail) => detail.value,
                  ),
                ]
                  .filter((part) => part !== "")
                  .join(" · ");
                return (
                  <li
                    key={key}
                    className="border-border-subtle flex flex-col gap-1 rounded-md border p-3"
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <Typography as="span" variant="body-sm" className="font-medium">
                        {t(itemLabelKey(key) as never)}
                      </Typography>
                      <Badge tone="neutral" variant="subtle" size="xs">
                        {type === "leave"
                          ? t("access.action.remove")
                          : t("access.wizard.review.held", { name: personName })}
                      </Badge>
                    </span>
                    {item && type === "new" && item.approval === "listed" ? (
                      <ListedOwnerLine
                        item={item.key}
                        resource={(answers.notes[key] ?? "").trim()}
                        owner={ownerOf(item)}
                      />
                    ) : (
                      <Typography as="span" variant="caption" tone="muted">
                        {item && (type === "leave" || stepOwnerKind(item) !== "it_setup")
                          ? `${t("access.wizard.review.form", { offering: item.offering })} · `
                          : ""}
                        {t("access.wizard.review.owner", { owner: ownerOf(item) })}
                      </Typography>
                    )}
                    {roles.length > 0 ? (
                      <Typography as="span" variant="caption">
                        {t("access.wizard.review.roles", { roles: roles.join("; ") })}
                      </Typography>
                    ) : null}
                    {note ? (
                      <Typography as="span" variant="caption">
                        {t("access.wizard.review.note", { note })}
                      </Typography>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            <Button variant="ghost" size="sm" className="self-start" onClick={onItems}>
              {t("access.wizard.review.editItems")}
            </Button>
          </>
        ) : null}
        {duplicate !== null ? (
          <>
            <DuplicateNotice found={duplicate} />
            <Button variant="outline" size="sm" className="self-start" onClick={onWho}>
              {t("access.duplicate.back")}
            </Button>
          </>
        ) : send.isError ? (
          <ErrorState variant="inline" error={send.error} />
        ) : null}
        {partial !== null ? (
          <Alert tone="warning" title={t("access.wizard.review.partialTitle")}>
            <ul className="m-0 list-disc ps-5">
              {(partial.failed ?? []).map((failure) => (
                <li key={failure.item}>
                  {t(itemLabelKey(failure.item) as never)}:{" "}
                  {failure.ref
                    ? tErrors("logged.item", { ref: failure.ref })
                    : tErrors("logged.description")}
                </li>
              ))}
            </ul>
            {t("access.wizard.review.partialHint")}
          </Alert>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="ghost"
            tone="neutral"
            onClick={() => onEdit(sequence.length - 1)}
            disabled={send.isPending}
          >
            {t("access.wizard.back")}
          </Button>
          <Button
            onClick={handleSend}
            loading={send.isPending}
            disabled={send.isPending || body.items.length !== sequence.length}
          >
            {partial === null ? t("access.wizard.review.send") : t("access.wizard.review.retry")}
          </Button>
          {partial !== null ? (
            <Button variant="outline" tone="neutral" onClick={() => onSent(partial)}>
              {t("access.wizard.review.continue")}
            </Button>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
}

/**
 * A new hire's software or folder on "Check and send" (13 Oct): who sets it
 * up, and, when its title or path is listed in Resource owners, that its
 * owner approves it first (asked on the New User Request at Send).
 */
function ListedOwnerLine({
  item,
  resource,
  owner,
}: {
  item: string;
  resource: string;
  /** Who sets it up ("Global IT Helpdesk, with the account"). */
  owner: string;
}) {
  const { t } = useT("tickets");
  const listed = useResourceOwner(item, resource, true);
  const approver = listed.data?.owner ?? null;
  return (
    <Typography as="span" variant="caption" tone="muted">
      {approver
        ? t("access.wizard.review.ownerApprovesFirst", { owner: approver })
        : t("access.wizard.review.owner", { owner })}
    </Typography>
  );
}

function SentSection({
  result,
  personName,
  type,
}: {
  result: AccessCaseSubmitResponse;
  personName: string;
  type: AccessTypeName;
}) {
  const { t } = useT("tickets");
  return (
    <SectionCard title={t("access.wizard.sent.title")}>
      <div className="flex flex-col gap-4">
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {(result.filed ?? []).map((filed) => (
            <li key={filed.ticket_id} className="flex items-center gap-2">
              <Icon icon={Check} size="sm" tone="success" />
              <Link to={filed.url_path} className="text-accent-fg-subtle underline">
                {t("access.wizard.sent.filed", {
                  item: t(itemLabelKey(filed.item) as never),
                  number: filed.ticket_number,
                })}
              </Link>
            </li>
          ))}
          {(result.held ?? []).map((key) => (
            <li key={key} className="flex items-center gap-2">
              <Icon icon={Hourglass} size="sm" tone="info" />
              {type === "leave"
                ? t("access.wizard.sent.removal", { item: t(itemLabelKey(key) as never) })
                : type === "change"
                  ? // 13 Oct (new forms): kept in the HelpDesk until approved, then filed.
                    t("access.wizard.sent.approval", { item: t(itemLabelKey(key) as never) })
                  : t("access.wizard.sent.held", {
                      item: t(itemLabelKey(key) as never),
                      name: personName,
                    })}
            </li>
          ))}
        </ul>
        {result.note_failed ? (
          <Alert tone="warning">{t("access.wizard.sent.noteFailed")}</Alert>
        ) : null}
        {result.case_id ? (
          <Button className="self-start" render={<Link to={accessCasePath(result.case_id)} />}>
            {t("access.wizard.sent.openCase")}
          </Button>
        ) : null}
      </div>
    </SectionCard>
  );
}

function StepButtons({
  onBack,
  onNext,
  nextLabel,
  nextDisabled = false,
}: {
  onBack?: () => void;
  onNext: () => void;
  /** "Next", or "Next · 2 still needed". */
  nextLabel?: string;
  nextDisabled?: boolean;
}) {
  const { t } = useT("tickets");
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {onBack === undefined ? null : (
        <Button variant="ghost" tone="neutral" onClick={onBack}>
          {t("access.wizard.back")}
        </Button>
      )}
      <Button onClick={onNext} disabled={nextDisabled}>
        {nextLabel ?? t("access.wizard.next")}
      </Button>
    </div>
  );
}
